import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Check, ChevronDown, Download, Menu, X, Target, Lightbulb,
  TrendingUp, Handshake, Cloud, Server, Brain, Code2, Shield, Database,
  Clock, Award, Users, Star, Quote, Sparkles, Zap, Building2, Play,
  ArrowUpRight, CircleDot, Layers, GitBranch, Rocket, Briefcase,
  UserCheck, ThumbsUp, Calendar, FileCheck, BarChart, ClipboardList,
  FileText, GitPullRequest, CheckCircle, Timer, DollarSign, Package,
  Settings, RefreshCw, Layout, Monitor, ShieldCheck, Zap as Lightning,
  CalendarDays, Gauge, PieChart, Rocket as RocketIcon, AlertTriangle,
  Sparkles as SparklesIcon, Layers as LayersIcon, Smartphone, Code,
  Cloud as CloudIcon, Database as DatabaseIcon, PenTool, Scale,
  UserPlus, Clipboard, ThumbsUp as ThumbsUpIcon, Repeat,
  GitBranch as GitBranchIcon, Users as UsersIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// ============ COLOR PALETTE ============
const COLORS = {
  primary: '#001534',
  secondary: '#FFFFFF',
  accent: '#6366F1',
  accentLight: '#818CF8',
  accentDark: '#4F46E5',
  purple: '#A855F7',
  cyan: '#06B6D4',
  white: '#FFFFFF',
  black: '#000000',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
  gradientStart: '#6366F1',
  gradientEnd: '#A855F7',
};

// ============ ANIMATED COUNTER ============
const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = end / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// ============ HERO ============
const Hero = () => {
  return (
    <section
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={{ backgroundColor: COLORS.primary }}
    >
      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.accent }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.purple }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(${COLORS.white} 1px, transparent 1px), linear-gradient(90deg, ${COLORS.white} 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.05] tracking-wide mb-6">
              <span style={{ color: COLORS.white }}>Contract-to-Hire:</span>
              <br />
              <span style={{ color: COLORS.white }}>Evaluate Before You </span>
              <span
                className="relative inline-block"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accentLight}, ${COLORS.purple})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Commit
              </span>
            </h1>

            <p className="text-lg md:text-xl leading-relaxed mb-10 max-w-xl" style={{ color: 'rgba(255,255,255,0.7)',wordBreak: 'normal',overflowWrap: 'normal',hyphens: 'none',whieSpace: 'normal' }}>
              Reduce hiring risk with trial employment. Convert top contractors to permanent employees after 3-6 month evaluation.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button
                className="group flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base transition-all hover:shadow-2xl hover:shadow-indigo-500/40"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
                  color: COLORS.white,
                }}
              >
                Start Evaluation
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                className="flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base border transition-all"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: COLORS.white,
                }}
              >
                Learn Benefits
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: 85, suffix: '%', label: 'Conversion Rate' },
                { value: 6, suffix: ' mos', label: 'Average Evaluation Period' },
                { value: 500, suffix: '+', label: 'Successful Conversions' },
                { value: 30, suffix: '%', label: 'Reduced Hiring Risk' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="border-l-2 pl-4"
                  style={{ borderColor: COLORS.accent }}
                >
                  <div className="text-3xl font-semibold" style={{ color: COLORS.white }}>
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} prefix={stat.prefix || ''} />
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image */}
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[400px]">
              <img
                src="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Staff%20Augementation/contracttohire.avif"
                alt="Contract-to-hire evaluation process"
                className="w-full h-full object-fit"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(168, 85, 247, 0.2))`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ WHY CONTRACT-TO-HIRE ============
const WhyContractToHire = () => {
  const features = [
    {
      icon: Target,
      title: 'Risk Reduction',
      desc: 'Test technical skills, cultural fit, and team compatibility before permanent commitment. No hidden surprises in work quality or performance.',
      color: COLORS.accent,
    },
    {
      icon: RefreshCw,
      title: 'True Evaluation Period',
      desc: '3-6 month trial gives you real-world performance data. Contractors contribute immediately while you assess fit for your team\'s long-term needs.',
      color: COLORS.purple,
    },
    {
      icon: DollarSign,
      title: 'Cost-Effective',
      desc: 'Lower contractor rates initially than permanent salaries. If successful conversion happens, standard permanent salary applies. No duplication of costs.',
      color: COLORS.cyan,
    },
    {
      icon: Lightning,
      title: 'Fast Onboarding',
      desc: 'Contractors are ready immediately. No 2-4 week onboarding typical of permanent hires. They\'re productive from day one.',
      color: '#F59E0B',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="why-cth"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-7xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Why Contract-to-Hire is Smart Hiring
          </h2>
          <p className="text-lg leading-relaxed max-w-3xl mx-auto" style={{ color: COLORS.gray600 }}>
            Reduce risk while maintaining hiring flexibility. Evaluate contractors before making permanent commitments.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative p-8 md:p-10 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                <div
                  className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-20"
                  style={{ backgroundColor: feat.color }}
                />

                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{
                      background: `linear-gradient(135deg, ${feat.color}30, ${feat.color}10)`,
                      border: `1px solid ${feat.color}40`,
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: feat.color }} />
                  </div>

                  <h3 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary }}>
                    {feat.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: COLORS.gray600 }}>
                    {feat.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ PROCESS ============
const Process = () => {
  const steps = [
    {
      number: '1',
      title: 'Define Evaluation Criteria',
      desc: 'Set clear success metrics and expectations for the trial period.',
      icon: ClipboardList,
      color: COLORS.accent,
    },
    {
      number: '2',
      title: 'Rapid Contractor Placement',
      desc: '24-48 hour candidate presentation. Start within 5-7 days.',
      icon: Users,
      color: COLORS.purple,
    },
    {
      number: '3',
      title: 'Evaluate Performance',
      desc: '3-6 month trial period. Monitor technical work, team fit, productivity.',
      icon: Gauge,
      color: COLORS.cyan,
    },
    {
      number: '4',
      title: 'Make Decision',
      desc: 'Convert to permanent hire or end engagement. No long-term commitment.',
      icon: Scale,
      color: '#F59E0B',
    },
    {
      number: '5',
      title: 'Smooth Transition',
      desc: 'Convert to permanent employment or leverage our network for replacement.',
      icon: Handshake,
      color: '#10B981',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="process"
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Contract-to-Hire Process
          </h2>
          <p className="text-lg text-center" style={{ color: COLORS.gray600 }}>
            Simple 5-step path from contract to permanent employment.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="relative p-6 rounded-3xl border text-center transition-all"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-1/2 -right-3 w-6 h-px"
                    style={{ backgroundColor: COLORS.gray300 }}
                  />
                )}

                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: `linear-gradient(135deg, ${step.color}30, ${step.color}10)`,
                    border: `1px solid ${step.color}40`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: step.color }} />
                </div>

                <div
                  className="text-md font-bold mb-2"
                  style={{ color: step.color }}
                >
                  Step {step.number}
                </div>

                <h3 className="text-md font-bold mb-2" style={{ color: COLORS.primary }}>
                  {step.title}
                </h3>
                <p className="text-md leading-relaxed" style={{ color: COLORS.gray600,wordBreak: 'normal',overflowWrap: 'normal',hyphens: 'none',whieSpace: 'normal' }}>
                  {step.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ WHEN CONTRACT-TO-HIRE WORKS BEST ============
const WhenToUse = () => {
  const scenarios = [
    {
      icon: AlertTriangle,
      title: 'Uncertain About Long-Term Need',
      desc: "You're not sure if the role will remain permanent after a year. Contract-to-hire lets you validate the need before making permanent commitment.",
      color: COLORS.accent,
    },
    {
      icon: Building2,
      title: 'First-Time Team Building',
      desc: 'Hiring into a new domain or building a new team? Trial period reduces risk and ensures you get the right people before permanent investment.',
      color: COLORS.purple,
    },
    {
      icon: Brain,
      title: 'Critical Skill Evaluation',
      desc: 'Need specialized expertise (blockchain, AI/ML, etc.) but want to evaluate quality? Contract-to-hire validates technical depth and project delivery.',
      color: COLORS.cyan,
    },
    {
      icon: DollarSign,
      title: 'Budget Flexibility',
      desc: 'Contractor rates are typically 20-30% higher but for fixed duration. Evaluate before committing permanent headcount to budget.',
      color: '#F59E0B',
    },
    {
      icon: Users,
      title: 'Team Integration Concerns',
      desc: 'Worried about cultural fit or team chemistry? Live evaluation period removes guesswork from hiring decisions.',
      color: '#10B981',
    },
    {
      icon: TrendingUp,
      title: 'Project Scaling',
      desc: 'Project work with potential for permanent role? Contractors contribute immediately while you assess if they\'re right for core team.',
      color: '#EC4899',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.primary }}
      id="when-to-use"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.white }}>
            When Contract-to-Hire Works Best
          </h2>
          <p className="text-lg text-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Ideal scenarios for using contract-to-hire staffing model.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {scenarios.map((scenario, i) => {
            const Icon = scenario.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="group p-8 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderColor: 'rgba(255,255,255,0.1)',
                }}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                  style={{
                    background: `${scenario.color}20`,
                    border: `1px solid ${scenario.color}40`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: scenario.color }} />
                </div>

                <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.white }}>
                  {scenario.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {scenario.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ CONVERSION PROCESS ============
const ConversionProcess = () => {
  const steps = [
    {
      title: 'Evaluation Phase (3-6 Months)',
      desc: 'Contractor works alongside your team. You assess technical skills, productivity, cultural fit, and team collaboration. Regular check-ins happen at months 1, 3, and 6.',
      icon: Clipboard,
      color: COLORS.accent,
    },
    {
      title: 'Conversion Decision (End of Month 6)',
      desc: 'Review performance against predefined success criteria. Decision to convert comes from your team based on real performance data, not interviews.',
      icon: Scale,
      color: COLORS.purple,
    },
    {
      title: 'Offer & Negotiation',
      desc: 'We help prepare competitive permanent offer. Salary typically transitions from contractor rate to permanent salary. Benefits and equity discussed.',
      icon: Handshake,
      color: COLORS.cyan,
    },
    {
      title: 'Employment Transition',
      desc: 'Contractor transitions to full-time employee status. No onboarding needed—they\'re already integrated with your team and systems.',
      icon: UserPlus,
      color: '#F59E0B',
    },
    {
      title: 'If Not Converting',
      desc: 'No hard feelings. We help find replacement from our network if needed. We maintain positive relationships and often help source additional contractors.',
      icon: RefreshCw,
      color: '#10B981',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="conversion"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Contract-to-Hire Conversion Process
          </h2>
          <p className="text-lg text-center" style={{ color: COLORS.gray600 }}>
            Smooth transition from contractor to permanent employee.
          </p>
        </motion.div>

        <div className="space-y-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex items-start gap-6 p-6 rounded-2xl border transition-all hover:shadow-lg"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 mt-1"
                  style={{
                    background: `${step.color}20`,
                    border: `1px solid ${step.color}40`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: step.color }} />
                </div>
                <div>
                  <h3 className="text-md font-bold mb-1" style={{ color: COLORS.primary }}>
                    {step.title}
                  </h3>
                  <p className="text-md leading-relaxed" style={{ color: COLORS.gray600 }}>
                    {step.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ FAQ ============
const FAQ = () => {
  const [open, setOpen] = useState(0);
  const faqs = [
    {
      q: 'How long is the typical contract-to-hire period?',
      a: 'Standard contract-to-hire periods are 3-6 months. Most common is 6 months, which provides sufficient time to evaluate technical skills, team fit, and project delivery. Shorter 3-month periods are available for specific evaluations.',
    },
    {
      q: 'What happens if we don\'t convert to permanent?',
      a: 'The contract simply ends. No penalties or additional costs. If you need a replacement, we help source additional contractors or direct hire candidates. We maintain positive relationships and often work with the contractor on future projects.',
    },
    {
      q: 'Can we extend the evaluation period beyond 6 months?',
      a: 'Yes! We can extend for additional months if you need more evaluation time. Contractor rates remain consistent. Most customers make conversion decisions by month 6, but flexibility is available for complex situations.',
    },
    {
      q: 'What\'s the typical salary difference from contractor to permanent?',
      a: 'Contract rates are typically 20-40% higher than permanent salary (because contractors pay their own benefits, taxes, insurance). Upon conversion to permanent, salary usually drops to market rate with added benefits (health, 401k, PTO). We provide transparent salary benchmarking for negotiation.',
    },
    {
      q: 'How fast can we start a contract-to-hire engagement?',
      a: 'Typically 24-48 hours to present candidates, 5-7 days to onboard first contractor. For urgent needs, we can expedite with pre-vetted candidates from our immediate pool. Faster start than permanent hiring but more vetted than casual contractor sourcing.',
    },
    {
      q: 'What if the contractor wants to leave before conversion?',
      a: 'Contractors can end engagement with standard notice (usually 2 weeks). If they leave before conversion, we help source replacement contractor or pivot to direct hire search. No penalties—we remain your staffing partner throughout.',
    },
  ];

  return (
    <section className="relative py-16 overflow-hidden" style={{ backgroundColor: COLORS.primary }} id="faq">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.secondary }}>
            Contract-to-Hire FAQs
          </h2>
        </motion.div>

        <div className="space-y-3 bg-white p-5 rounded-3xl border">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl border overflow-hidden transition-all"
              style={{
                backgroundColor: '#ffffff',
                borderColor: open === i ? 'rgba(99, 102, 241, 0.3)' : COLORS.gray200,
              }}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 p-6 text-left"
              >
                <span className="font-semibold text-base md:text-lg" style={{ color: COLORS.primary }}>
                  {faq.q}
                </span>
                <motion.div
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: open === i ? COLORS.accent : COLORS.gray200 }}
                >
                  <ChevronDown className="w-4 h-4" style={{ color: open === i ? COLORS.white : COLORS.primary }} />
                </motion.div>
              </button>

              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 leading-relaxed text-justify" style={{ color: COLORS.gray600, wordBreak: 'normal', overflowWrap: 'normal', hyphens: 'none', whiteSpace: 'normal' }}>
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ FINAL CTA ============
const FinalCTA = () => {
  const COLORS = {
    bg: '#F8FAFC',
    heading: '#0F172A',
    muted: '#475569',
    accent: '#6366F1',
    accentLight: '#A5B4FC',
    white: '#FFFFFF',
    border: '#E2E8F0',
  };

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{ backgroundColor: COLORS.bg }}
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6"
            style={{ color: COLORS.heading }}
          >
            Ready to Try Contract-to-Hire?
          </h2>

          <p
            className="text-base md:text-lg max-w-2xl mx-auto mb-10"
            style={{ color: COLORS.muted }}
          >
            Reduce hiring risk while maintaining flexibility. Start your evaluation period today.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              onClick={() => window.scrollTo(0, 0)}
              whileHover={{ scale: 1.03, y: -1 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base"
              style={{
                backgroundColor: COLORS.accent,
                color: COLORS.white,
              }}
            >
              Start Your Search
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============ MAIN APP ============
export default function ContractToHire() {
  return (
    <div style={{ backgroundColor: COLORS.secondary, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Hero />
      <WhyContractToHire />
      <Process />
      <WhenToUse />
      <ConversionProcess />
      <FAQ />
      <FinalCTA />
    </div>
  );
}
